import { Box, EmptyState, Table, Text } from '@chakra-ui/react'
import { LuHistory } from 'react-icons/lu'
import {
  actorLabel,
  formatDateTimeDE,
  formatEffectiveFromDE,
  formatValue,
  type HistoryRow,
  type ValueKind,
} from '@/helper/valueChange.helper'

type ValueChangeHistoryTableProps = {
  kind: ValueKind
  rows: HistoryRow[]
  currentUser: { id: string; displayName?: string | null } | null
}

/**
 * The SIB's own change history (SIB-941): service, old value, new value, effective date, changed
 * on, changed by. A dropped planned change stays in the list and says so; a change an
 * administrator recorded names the administrator, not the SIB.
 */
export function ValueChangeHistoryTable({
  kind,
  rows,
  currentUser,
}: ValueChangeHistoryTableProps) {
  if (rows.length === 0) {
    return (
      <EmptyState.Root>
        <EmptyState.Content>
          <EmptyState.Indicator>
            <LuHistory />
          </EmptyState.Indicator>
          <EmptyState.Title>Noch keine Änderungen</EmptyState.Title>
          <EmptyState.Description>
            Hier erscheinen Ihre Änderungen mit altem und neuem Wert, Datum und Bearbeiter.
          </EmptyState.Description>
        </EmptyState.Content>
      </EmptyState.Root>
    )
  }

  return (
    <Box overflowX="auto">
      <Table.Root size="sm" minW="720px" data-testid="value-change-history">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>Leistung</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="right">Alter Wert</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="right">Neuer Wert</Table.ColumnHeader>
            <Table.ColumnHeader>Gültig ab</Table.ColumnHeader>
            <Table.ColumnHeader>Geändert am</Table.ColumnHeader>
            <Table.ColumnHeader>Geändert von</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.map(row => (
            <Table.Row key={row.id} data-dropped={row.dropped ? 'true' : undefined}>
              <Table.Cell>{row.serviceLabel}</Table.Cell>
              <Table.Cell textAlign="right">{formatValue(kind, row.oldValue)}</Table.Cell>
              <Table.Cell textAlign="right">
                {row.dropped ? (
                  <Text as="s" color="fg.muted">
                    {formatValue(kind, row.value)}
                  </Text>
                ) : (
                  formatValue(kind, row.value)
                )}
              </Table.Cell>
              <Table.Cell>
                {formatEffectiveFromDE(row.validFrom)}
                {row.dropped && (
                  <Text color="fg.muted">
                    Verworfen am {formatDateTimeDE(row.discardedAt)} von{' '}
                    {actorLabel(row.discardedBy, currentUser)}
                  </Text>
                )}
              </Table.Cell>
              <Table.Cell>{formatDateTimeDE(row.createdAt)}</Table.Cell>
              <Table.Cell>{actorLabel(row.createdBy, currentUser)}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  )
}
